// Unified tracking utility for Google Analytics and Google Ads
// Integrated with cookie consent system

const TRACKING_CONFIG = {
  // Google Analytics Measurement IDs
  gaIds: ['G-M0KTNQG0FJ', 'G-JCF53FCNM6'],
  
  // Google Ads Conversion ID
  adsConversionId: 'AW-17788894410',
  
  // Google Ads Conversion Label (actual label for purchase conversion)
  adsConversionLabel: 'd874CPjukc4bEMr5s6JC',
  
  // Default currency for conversions
  defaultCurrency: 'EUR',
  
  // Privacy settings
  privacy: {
    anonymizeIp: true,
    allowGoogleSignals: false,
    allowAdPersonalizationSignals: false,
    restrictedDataProcessing: true
  }
};

// SHA-256 hash function for Enhanced Conversions
const sha256 = async (message) => {
  // Convert string to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  
  // Hash the data
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

// Hash email for Enhanced Conversions (lowercase and trim)
export const hashEmailForEnhancedConversions = async (email) => {
  if (!email || typeof email !== 'string') return null;
  
  try {
    const normalizedEmail = email.trim().toLowerCase();
    return await sha256(normalizedEmail);
  } catch (error) {
    console.error('Error hashing email for enhanced conversions:', error);
    return null;
  }
};

// Check if tracking is enabled based on cookie consent
export const isTrackingEnabled = () => {
  if (typeof window === 'undefined') return false;
  
  const cookies = document.cookie.split(';').map(c => c.trim());
  const consentCookie = cookies.find(c => c.startsWith('userConsent='));
  
  if (consentCookie) {
    const consentValue = consentCookie.split('=')[1];
    return consentValue === 'accepted';
  }
  
  return false;
};

// Load gtag.js script once
const loadGtagScript = () => {
  if (typeof window === 'undefined') return;
  
  // Check if script already loaded
  const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  if (existingScript) return;
  
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${TRACKING_CONFIG.gaIds[0]}`;
  script.async = true;
  document.head.appendChild(script);
  
  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
};

// Initialize all tracking (call after cookie consent is accepted)
export const initTracking = () => {
  if (typeof window === 'undefined' || !isTrackingEnabled()) return;
  
  loadGtagScript();
  
  // Configure Google Analytics for all IDs
  TRACKING_CONFIG.gaIds.forEach(id => {
    window.gtag('config', id, {
      anonymize_ip: TRACKING_CONFIG.privacy.anonymizeIp,
      allow_google_signals: TRACKING_CONFIG.privacy.allowGoogleSignals,
      allow_ad_personalization_signals: TRACKING_CONFIG.privacy.allowAdPersonalizationSignals
    });
  });
  
  // Configure Google Ads with Enhanced Conversions
  window.gtag('config', TRACKING_CONFIG.adsConversionId, {
    allow_google_signals: TRACKING_CONFIG.privacy.allowGoogleSignals,
    allow_ad_personalization_signals: TRACKING_CONFIG.privacy.allowAdPersonalizationSignals,
    restricted_data_processing: TRACKING_CONFIG.privacy.restrictedDataProcessing
  });
  
};

// Disable all tracking (call when cookie consent is denied or revoked)
export const disableTracking = () => {
  if (typeof window === 'undefined') return;
  
  // Remove gtag script
  const scripts = document.head.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src.includes('www.googletagmanager.com/gtag/js')) {
      document.head.removeChild(scripts[i]);
      break;
    }
  }
  
  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer = [];
  }
  
  // Remove gtag function
  delete window.gtag;
  
};

// Track page view for all configured IDs
export const trackPageView = (path) => {
  if (typeof window === 'undefined' || !window.gtag || !isTrackingEnabled()) return;
  
  TRACKING_CONFIG.gaIds.forEach(id => {
    window.gtag('config', id, {
      page_path: path,
      anonymize_ip: TRACKING_CONFIG.privacy.anonymizeIp
    });
  });
  
};

// Track donation conversion with Enhanced Conversions support
export const trackDonationConversion = async (amount, currency = TRACKING_CONFIG.defaultCurrency, transactionId = null, donorEmail = null) => {
  if (typeof window === 'undefined' || !window.gtag || !isTrackingEnabled()) return;
  
  const tId = transactionId || `donation_${Date.now()}`;
  
  // Prepare conversion data
  const conversionData = {
    'send_to': `${TRACKING_CONFIG.adsConversionId}/${TRACKING_CONFIG.adsConversionLabel}`,
    'value': amount,
    'currency': currency,
    'transaction_id': tId,
    'event_category': 'donation',
    'event_label': 'Donation Completed'
  };
  
  // Add Enhanced Conversions data if email is available
  if (donorEmail) {
    try {
      const hashedEmail = await hashEmailForEnhancedConversions(donorEmail);
      if (hashedEmail) {
        conversionData.user_data = {
          email_address: hashedEmail
        };
      }
    } catch (error) {
      console.warn('Failed to add enhanced conversion data:', error);
    }
  }
  
  // Google Ads conversion with Enhanced Conversions
  window.gtag('event', 'conversion', conversionData);
  
  // Enhanced e-commerce purchase event
  window.gtag('event', 'purchase', {
    'transaction_id': tId,
    'affiliation': 'YourNGO',
    'value': amount,
    'currency': currency,
    'tax': 0,
    'shipping': 0,
    'items': [{
      'item_id': 'donation',
      'item_name': 'Charitable Donation',
      'item_category': 'Donation',
      'price': amount,
      'quantity': 1
    }]
  });
  
  
  // Log to file
  const utmParams = {};
  const urlParams = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    const value = urlParams.get(key);
    if (value) utmParams[key] = value;
  });
  
  sendTrackingLog('donation_conversion', {
    amount,
    currency,
    transaction_id: tId,
    donor_email: donorEmail ? donorEmail.substring(0, 3) + '...' : null,
    has_enhanced_conversions: !!donorEmail
  }, Object.keys(utmParams).length > 0 ? utmParams : null, {
    conversion_id: TRACKING_CONFIG.adsConversionId,
    conversion_label: TRACKING_CONFIG.adsConversionLabel,
    value: amount,
    currency: currency
  });
};

// Track subscription conversion with Enhanced Conversions support
export const trackSubscriptionConversion = async (amount, currency = TRACKING_CONFIG.defaultCurrency, transactionId = null, subscriberEmail = null) => {
  if (typeof window === 'undefined' || !window.gtag || !isTrackingEnabled()) return;
  
  const tId = transactionId || `subscription_${Date.now()}`;
  
  // Prepare conversion data
  const conversionData = {
    'send_to': `${TRACKING_CONFIG.adsConversionId}/${TRACKING_CONFIG.adsConversionLabel}`,
    'value': amount,
    'currency': currency,
    'transaction_id': tId,
    'event_category': 'subscription',
    'event_label': 'Subscription Purchase'
  };
  
  // Add Enhanced Conversions data if email is available
  if (subscriberEmail) {
    try {
      const hashedEmail = await hashEmailForEnhancedConversions(subscriberEmail);
      if (hashedEmail) {
        conversionData.user_data = {
          email_address: hashedEmail
        };
      }
    } catch (error) {
      console.warn('Failed to add enhanced conversion data for subscription:', error);
    }
  }
  
  // Google Ads conversion with Enhanced Conversions
  window.gtag('event', 'conversion', conversionData);
  
  // Enhanced e-commerce purchase event
  window.gtag('event', 'purchase', {
    'transaction_id': tId,
    'affiliation': 'YourNGO',
    'value': amount,
    'currency': currency,
    'tax': 0,
    'shipping': 0,
    'items': [{
      'item_id': 'subscription',
      'item_name': 'Monthly Subscription',
      'item_category': 'Subscription',
      'price': amount,
      'quantity': 1
    }]
  });
  
  
  // Log to file
  const utmParams = {};
  const urlParams = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    const value = urlParams.get(key);
    if (value) utmParams[key] = value;
  });
  
  sendTrackingLog('subscription_conversion', {
    amount,
    currency,
    transaction_id: tId,
    subscriber_email: subscriberEmail ? subscriberEmail.substring(0, 3) + '...' : null,
    has_enhanced_conversions: !!subscriberEmail
  }, Object.keys(utmParams).length > 0 ? utmParams : null, {
    conversion_id: TRACKING_CONFIG.adsConversionId,
    conversion_label: TRACKING_CONFIG.adsConversionLabel,
    value: amount,
    currency: currency
  });
};

// Track custom event for analytics
export const trackEvent = (action, category, label, value = null) => {
  if (typeof window === 'undefined' || !window.gtag || !isTrackingEnabled()) return;
  
  const eventParams = {
    'event_category': category,
    'event_label': label
  };
  
  if (value !== null) {
    eventParams.value = value;
  }
  
  window.gtag('event', action, eventParams);
  
  
  // Log to file for important events
  const importantEvents = ['button_click', 'form_submission', 'page_view', 'donation_started', 'subscription_started'];
  if (importantEvents.includes(action)) {
    const utmParams = {};
    const urlParams = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
      const value = urlParams.get(key);
      if (value) utmParams[key] = value;
    });
    
    sendTrackingLog('custom_event', {
      action,
      category,
      label,
      value,
      page_url: window.location.href
    }, Object.keys(utmParams).length > 0 ? utmParams : null);
  }
};

// Track form submission with Enhanced Conversions support
export const trackFormSubmission = async (formName, formType = 'contact', userEmail = null) => {
  if (typeof window === 'undefined' || !window.gtag || !isTrackingEnabled()) return;
  
  const eventParams = {
    'event_category': 'form',
    'event_label': `${formType}_${formName}`
  };
  
  // Add Enhanced Conversions data if email is available
  if (userEmail) {
    try {
      const hashedEmail = await hashEmailForEnhancedConversions(userEmail);
      if (hashedEmail) {
        eventParams.user_data = {
          email_address: hashedEmail
        };
      }
    } catch (error) {
      console.warn('Failed to add enhanced conversion data for form submission:', error);
    }
  }
  
  window.gtag('event', 'form_submission', eventParams);
  
  
  // Log to file
  const utmParams = {};
  const urlParams = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    const value = urlParams.get(key);
    if (value) utmParams[key] = value;
  });
  
  sendTrackingLog('form_submission', {
    form_name: formName,
    form_type: formType,
    user_email: userEmail ? userEmail.substring(0, 3) + '...' : null,
    has_enhanced_conversions: !!userEmail,
    page_url: window.location.href
  }, Object.keys(utmParams).length > 0 ? utmParams : null);
};

// Track button click for remarketing
export const trackButtonClick = (buttonName, buttonLocation) => {
  trackEvent('button_click', 'engagement', buttonName);
};

// Initialize tracking if consent is given (call on page load)
export const initTrackingIfConsented = () => {
  if (isTrackingEnabled()) {
    initTracking();
  }
};

// Update conversion label (call when you get the actual label)
export const updateConversionLabel = (newLabel) => {
  TRACKING_CONFIG.adsConversionLabel = newLabel;
};

// Get current tracking configuration
export const getTrackingConfig = () => {
  return { ...TRACKING_CONFIG };
};

// Send tracking log to server for file logging
const sendTrackingLog = async (eventType, eventDetails = {}, utmParams = null, conversionData = null) => {
  if (typeof window === 'undefined') return;
  
  try {
    const logData = {
      event_type: eventType,
      event_details: eventDetails,
      utm_params: utmParams,
      conversion_data: conversionData
    };
    
    // Send to server endpoint
    const response = await fetch('/donation_handler.php?action=tracking_log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': 'DONATION_4f7a9b2c8d3e1f6a5c9b8d7e2f3a4c6b'
      },
      body: JSON.stringify(logData)
    });
    
    if (!response.ok) {
      console.warn('Failed to send tracking log to server:', response.status);
    }
  } catch (error) {
    console.warn('Error sending tracking log:', error);
  }
};

// Track UTM parameters for better attribution
export const trackUTMParameters = () => {
  if (typeof window === 'undefined' || !window.gtag || !isTrackingEnabled()) return;
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  
  // Common UTM parameters
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  // If we have UTM parameters, send them as an event
  if (Object.keys(utmParams).length > 0) {
    window.gtag('event', 'utm_parameters_received', {
      ...utmParams,
      event_category: 'attribution',
      event_label: 'UTM Parameters'
    });
    
    
    // Log to file
    sendTrackingLog('utm_parameters_received', {
      parameters: utmParams,
      page_url: window.location.href
    }, utmParams);
  }
};

// Initialize UTM tracking on page load
export const initUTMTracking = () => {
  if (typeof window === 'undefined') return;
  
  // Track UTM parameters on initial page load
  trackUTMParameters();
  
  // Also track UTM parameters if they change via History API
  const originalPushState = window.history.pushState;
  window.history.pushState = function(...args) {
    originalPushState.apply(this, args);
    setTimeout(trackUTMParameters, 100);
  };
  
  const originalReplaceState = window.history.replaceState;
  window.history.replaceState = function(...args) {
    originalReplaceState.apply(this, args);
    setTimeout(trackUTMParameters, 100);
  };
};
